import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Package, ArrowLeft, User, Target, TrendingUp, Loader } from 'lucide-react';
import SEO from './SEO';
import { supabase } from '../config/supabase';
import { GolfClub } from '../types';

interface PublicProfile {
  id: string;
  firstName: string;
  lastName?: string;
  handicap?: number;
  skillLevel?: string;
  golfBag: GolfClub[];
  isPublic: boolean;
}

// Group clubs by category for display
const groupClubsByCategory = (clubs: GolfClub[]) => {
  const groups: Record<string, GolfClub[]> = {
    'Driver & Woods': [],
    'Hybrids': [],
    'Irons': [],
    'Wedges': [],
    'Putter': []
  };

  clubs.forEach(club => {
    const type = club.type.toLowerCase();
    if (type.includes('driver') || type.includes('wood')) {
      groups['Driver & Woods'].push(club);
    } else if (type.includes('hybrid')) {
      groups['Hybrids'].push(club);
    } else if (type.includes('iron') || club.type.match(/^\d+I$/)) {
      groups['Irons'].push(club);
    } else if (type.includes('wedge') || ['PW', 'GW', 'SW', 'LW'].includes(club.type)) {
      groups['Wedges'].push(club);
    } else if (type.includes('putter')) {
      groups['Putter'].push(club);
    }
  });

  // Sort irons numerically
  groups['Irons'].sort((a, b) => {
    const numA = parseInt(a.type) || 99;
    const numB = parseInt(b.type) || 99;
    return numA - numB;
  });

  return groups;
};

const PublicBagView: React.FC = () => {
  const { bagId } = useParams<{ bagId: string }>();
  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPublicBag = async () => {
      if (!bagId) {
        setError('Invalid bag ID');
        setLoading(false);
        return;
      }

      try {
        // Fetch the public profile
        const { data, error: fetchError } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, handicap, skill_level, golf_bag, is_public')
          .eq('id', bagId)
          .eq('is_public', true)
          .single();

        if (fetchError || !data) {
          setError('This bag is private or does not exist');
          setLoading(false);
          return;
        }

        setProfile({
          id: data.id,
          firstName: data.first_name || 'Golfer',
          lastName: data.last_name,
          handicap: data.handicap,
          skillLevel: data.skill_level,
          golfBag: data.golf_bag || [],
          isPublic: data.is_public
        });
      } catch (err) {
        setError('Failed to load bag');
        console.error('Error fetching public bag:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPublicBag();
  }, [bagId]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-green-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading bag...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !profile) {
    return (
      <>
        <SEO 
          title="Bag Not Found"
          description="This golf bag is private or doesn't exist."
          noIndex={true}
        />
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Bag Not Found</h1>
            <p className="text-gray-600 mb-6">{error || 'This bag is private or does not exist.'}</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors font-medium"
            >
              <ArrowLeft className="w-5 h-5" />
              Go to ClubIQ
            </Link>
          </div>
        </div>
      </>
    );
  }

  const clubs = profile.golfBag;
  const groupedClubs = groupClubsByCategory(clubs);
  const driverDistance = clubs.find(c => c.type === 'driver')?.distance;
  const sevenIronDistance = clubs.find(c => c.type === '7-iron')?.distance;

  const getHandicapDisplay = () => {
    if (profile.handicap !== undefined) {
      return profile.handicap === 0 ? 'Scratch' : 
             profile.handicap > 0 ? `+${profile.handicap}` : 
             profile.handicap.toString();
    }
    return profile.skillLevel || 'N/A';
  };

  return (
    <>
      <SEO 
        title={`${profile.firstName}'s Golf Bag`}
        description={`Check out ${profile.firstName}'s golf bag on ClubIQ - ${clubs.length} clubs including ${clubs.find(c => c.type === 'driver')?.brand || 'a'} driver. AI-powered golf equipment recommendations.`}
        url={`/bag/${bagId}`}
      />
      
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
          <div className="max-w-4xl mx-auto px-4 py-8">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">ClubIQ Home</span>
            </Link>
            
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                <User className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold">
                  {profile.firstName}'s Golf Bag
                </h1>
                <p className="text-white/80">{clubs.length} clubs in the bag</p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="max-w-4xl mx-auto px-4 -mt-6">
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white rounded-xl shadow-lg p-4 text-center">
              <Target className="w-6 h-6 text-green-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900">{getHandicapDisplay()}</div>
              <div className="text-xs text-gray-500">Handicap</div>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-4 text-center">
              <TrendingUp className="w-6 h-6 text-blue-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900">{driverDistance || '--'}</div>
              <div className="text-xs text-gray-500">Driver (yds)</div>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-4 text-center">
              <Package className="w-6 h-6 text-purple-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900">{sevenIronDistance || '--'}</div>
              <div className="text-xs text-gray-500">7-Iron (yds)</div>
            </div>
          </div>
        </div>

        {/* Club List */}
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="space-y-6">
            {Object.entries(groupedClubs).map(([category, categoryClubs]) => 
              categoryClubs.length > 0 && (
                <div key={category} className="bg-white rounded-xl shadow-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b">
                    <h2 className="font-semibold text-gray-900">{category}</h2>
                  </div>
                  <div className="divide-y">
                    {categoryClubs.map((club, idx) => (
                      <div key={idx} className="px-4 py-3 flex items-center justify-between">
                        <div>
                          <div className="font-medium text-gray-900">
                            {club.brand && `${club.brand} `}{club.model || club.type}
                          </div>
                          <div className="text-sm text-gray-500">
                            {club.type}
                            {club.loft && ` • ${club.loft}°`}
                            {club.shaftType && ` • ${club.shaftType}`}
                          </div>
                        </div>
                        {club.distance && (
                          <div className="text-right">
                            <div className="font-semibold text-gray-900">{club.distance} yds</div>
                            <div className="text-xs text-gray-500">Carry</div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )
            )}
          </div>

          {/* CTA */}
          <div className="mt-8 bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl p-6 text-white text-center">
            <h3 className="text-xl font-bold mb-2">Build Your Perfect Bag</h3>
            <p className="text-green-100 mb-4">
              Get AI-powered recommendations tailored to your game
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-white text-green-700 px-6 py-3 rounded-lg hover:bg-green-50 transition-colors font-semibold"
            >
              Try ClubIQ Free
            </Link>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-900 text-gray-400 py-6 text-center text-sm">
          <p>Powered by <Link to="/" className="text-white hover:underline">ClubIQ</Link> - AI Golf Assistant</p>
        </div>
      </div>
    </>
  );
};

export default PublicBagView;

